import React from "react";
// ✅ IMPORT YOUR ICON LOGO
import IconLogo from "../assets/icon logo.png"; 

const AdminLoader = ({ message = "Initializing Dashboard..." }) => {
    return (
        <div className="admin-loader-overlay">
            <style>{`
                /* --- Core Variables --- */
                :root {
                    --admin-bg-dark: #0f172a;
                    --admin-bg-light: #1e293b;
                    --brand-gold: #f59e0b;
                    --brand-blue: #3b82f6;
                }

                /* --- Layout & Background --- */
                .admin-loader-overlay {
                    position: fixed;
                    inset: 0;
                    background: radial-gradient(circle at center, var(--admin-bg-light) 0%, var(--admin-bg-dark) 100%);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    font-family: 'Inter', system-ui, -apple-system, sans-serif;
                    /* Smooth Entry Animation */
                    animation: fadeInOverlay 0.5s ease-out;
                }

                @keyframes fadeInOverlay {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                /* Grid Background Pattern */
                .admin-loader-overlay::before {
                    content: "";
                    position: absolute;
                    inset: 0;
                    background-image: linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
                                      linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
                    background-size: 40px 40px;
                    opacity: 0.4;
                    z-index: -1;
                }

                /* --- The Central HUD Element --- */
                .loader-hud {
                    position: relative;
                    width: 140px;
                    height: 140px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                /* Rotating Radar Ring */
                .hud-ring {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    border: 2px dashed rgba(245, 158, 11, 0.3); /* Gold dashed */
                    border-radius: 50%;
                    animation: spin-slow 10s linear infinite;
                }
                
                .hud-ring::after {
                    content: "";
                    position: absolute;
                    top: -2px;
                    left: 50%;
                    width: 10px;
                    height: 10px;
                    background: var(--brand-gold);
                    border-radius: 50%;
                    box-shadow: 0 0 15px var(--brand-gold);
                    transform: translateX(-50%);
                }

                /* Inner Pulse Circle */
                .hud-pulse {
                    position: absolute;
                    width: 85%;
                    height: 85%;
                    border: 1px solid rgba(59, 130, 246, 0.3); /* Blue */
                    border-radius: 50%;
                    animation: ping-gentle 2.5s cubic-bezier(0, 0, 0.2, 1) infinite;
                }

                /* --- The Hexagon Container --- */
                .hex-base {
                    position: relative;
                    width: 80px; /* Slightly larger for your logo */
                    height: 80px;
                    background: linear-gradient(135deg, var(--brand-gold) 0%, #d97706 100%);
                    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10;
                    box-shadow: 0 0 40px rgba(245, 158, 11, 0.5);
                    /* Subtle float animation */
                    animation: float-logo 3s ease-in-out infinite;
                }

                /* Inner Glass Hexagon */
                .hex-glass {
                    width: 76px;
                    height: 76px;
                    background: rgba(0, 0, 0, 0.3);
                    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    backdrop-filter: blur(4px);
                }

                /* --- LOGO STYLING --- */
                .custom-logo-loader {
                    width: 45px;
                    height: 45px;
                    object-fit: contain;
                    filter: brightness(0) invert(1); /* Makes logo pure white */
                    animation: logo-breathe 2s ease-in-out infinite;
                }

                /* --- Animations --- */
                @keyframes spin-slow {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                @keyframes ping-gentle {
                    0% { transform: scale(0.85); opacity: 0.6; }
                    70% { transform: scale(1.1); opacity: 0; }
                    100% { transform: scale(0.85); opacity: 0; }
                }

                @keyframes float-logo {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-4px); }
                }

                @keyframes logo-breathe {
                    0%, 100% { transform: scale(1); filter: brightness(0) invert(1) drop-shadow(0 0 2px rgba(255,255,255,0.5)); }
                    50% { transform: scale(1.1); filter: brightness(0) invert(1) drop-shadow(0 0 8px rgba(255,255,255,0.8)); }
                }

                /* --- Typography --- */
                .admin-text-container {
                    margin-top: 40px;
                    text-align: center;
                    animation: fadeInUp 0.6s ease-out 0.2s backwards;
                }

                .brand-title {
                    font-size: 24px;
                    font-weight: 800;
                    letter-spacing: 2px;
                    color: white;
                    text-transform: uppercase;
                }

                .brand-highlight {
                    color: var(--brand-gold);
                }

                .loading-status {
                    margin-top: 10px;
                    font-size: 11px;
                    font-weight: 600;
                    letter-spacing: 3px;
                    color: #94a3b8;
                    text-transform: uppercase;
                    position: relative;
                    overflow: hidden;
                    display: inline-block;
                }

                .loading-status::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
                    animation: text-scan 2s infinite linear;
                }

                @keyframes text-scan {
                    0% { left: -100%; }
                    100% { left: 100%; }
                }
                
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>

            {/* Visual HUD Element */}
            <div className="loader-hud">
                <div className="hud-ring"></div>
                <div className="hud-pulse"></div>
                
                <div className="hex-base">
                    <div className="hex-glass">
                        {/* ✅ REPLACED ICON WITH YOUR LOGO IMAGE */}
                        <img 
                            src={IconLogo} 
                            alt="Loading..." 
                            className="custom-logo-loader" 
                        />
                    </div>
                </div>
            </div>

            {/* Text & Status */}
            <div className="admin-text-container">
                <div className="brand-title">
                    PATRA <span className="brand-highlight">ADMIN</span>
                </div>
                <div className="loading-status">
                    {message}
                </div>
            </div>
        </div>
    );
};

export default AdminLoader;